// frontend/src/DepartmentMessagesPortal.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import './DepartmentMessagesPortal.css';

const POLL_MS = 30_000; // 30s
const PATIENT_SEARCH_DEBOUNCE_MS = 250;

const DEPT_OPTIONS = [
  { value: 'image_processing', labelKey: 'department.imageProcessing' },
  { value: 'clinical_support', labelKey: 'department.clinicalSupport' },
  { value: 'manufacturing', labelKey: 'department.manufacturing' },
];

const TEMPLATE_PRESETS = [
  { key: '', labelKey: 'deptMsg.noTemplate', subjectKey: '', bodyKey: '' },
  {
    key: 'dsi_incorrect',
    labelKey: 'deptMsg.dsiIncorrect',
    subjectKey: 'deptMsg.dsiSubject',
    bodyKey: 'deptMsg.dsiBody',
  },
];

const TRANSLATE_TARGETS = [
  { value: 'zh-Hans', labelKey: 'translate.chineseSimplified' },
  { value: 'en', labelKey: 'translate.english' },
];

// ----- helpers -----
function safeDate(dateStr, locale) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString(locale);
  } catch {
    return dateStr;
  }
}

function buildAuthHeaders({ mode, token }) {
  if (!token) return {};
  if (mode === 'manager') return { 'X-Manager-Token': token };
  return { 'X-Dept-Token': token };
}

function deptLabelFromValue(dept) {
  const found = DEPT_OPTIONS.find((d) => d.value === dept);
  return found?.labelKey || dept || 'department.department';
}

function readJsonLocal(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJsonLocal(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function extractTranslatedText(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;

  if (typeof data.translated_text === 'string') return data.translated_text;
  if (typeof data.translation === 'string') return data.translation;
  if (typeof data.text === 'string') return data.text;

  if (Array.isArray(data.translations) && data.translations[0]?.text) {
    return data.translations[0].text;
  }
  if (Array.isArray(data) && data[0]?.translations?.[0]?.text) {
    return data[0].translations[0].text;
  }

  if (data.result && typeof data.result === 'string') return data.result;
  return '';
}

function patientLabel(p, fallbackLabel = 'Patient') {
  if (!p) return '';
  const candidates = [
    p.display,
    p.patient_display,
    p.patientDisplay,
    p.label,
    p.name,
    p.full_name,
    p.fullName,
    p.patient_name,
    p.patientName,
  ].filter(Boolean);

  for (const c of candidates) {
    const s = String(c).trim();
    if (s) return s;
  }

  const first = p.first_name || p.firstName || p.given_name || p.givenName || '';
  const last = p.last_name || p.lastName || p.family_name || p.familyName || '';
  const middle = p.middle_name || p.middleName || '';
  const lastFirst = [last, first, middle].filter(Boolean).join(' ').trim();
  if (lastFirst) return lastFirst;

  const firstLast = [first, middle, last].filter(Boolean).join(' ').trim();
  if (firstLast) return firstLast;

  const mrn = p.mrn || p.patient_id || p.patientId || p.external_id || p.externalId;
  if (mrn) return `${fallbackLabel} ${mrn}`;

  return p.id ? `${fallbackLabel} #${p.id}` : fallbackLabel;
}

function newClientRequestId(prefix = 'req') {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return `${prefix}_${crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

//  central helper: make hidden flags future-proof (thread-level)
function appendHiddenFlags(formData, valueBool) {
  const v = String(!!valueBool);
  formData.append('is_hidden', v);
  formData.append('hidden', v);
  formData.append('hide_from_patient', v);
}

//  central helper: hidden flags future-proof (reply-level)
function appendHiddenReplyFlags(formData, valueBool) {
  if (!valueBool) return;
  formData.append('is_hidden_from_patient', 'true');
  formData.append('hidden_from_patient', 'true');
  formData.append('hide_reply_from_patient', 'true');
}

//  robust parsing for hidden flags coming back from API
function truthy(val) {
  return val === true || val === 1 || val === '1' || String(val).toLowerCase() === 'true';
}

function isThreadHidden(thread) {
  if (!thread) return false;
  return (
    truthy(thread.is_hidden) ||
    truthy(thread.hidden) ||
    truthy(thread.hide_from_patient) ||
    truthy(thread.isHidden)
  );
}

function isReplyHidden(r) {
  if (!r) return false;
  return (
    truthy(r.is_hidden_from_patient) ||
    truthy(r.hidden_from_patient) ||
    truthy(r.hide_reply_from_patient) ||
    truthy(r.isHiddenFromPatient)
  );
}

function ThreadPill({ children, tone = 'neutral' }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

function ConversationBubble({
  t,
  side = 'left',
  header,
  subheader,
  time,
  body,
  attachmentUrl,
  onTranslate,
  translateKey,
  translatedText,
  isTranslating,
  showTranslation,
  onToggleTranslation,
  badges = [],
}) {
  const hasText = (body || '').trim().length > 0;
  const hasTranslation = (translatedText || '').trim().length > 0;

  return (
    <div className={`bubble-row ${side}`}>
      <div className={`bubble ${side}`}>
        <div className="bubble-top">
          <div className="bubble-header-wrap">
            <div className="bubble-header">
              {header}
              {badges?.length ? (
                <span className="bubble-badges">
                  {badges.map((b) => (
                    <span
                      key={b}
                      className={`bubble-badge ${String(b).toLowerCase().includes('hidden') ? 'hidden' : ''}`}
                    >
                      {b}
                    </span>
                  ))}
                </span>
              ) : null}
            </div>
            {subheader ? <div className="bubble-subheader">{subheader}</div> : null}
          </div>
          <div className="bubble-time">{time}</div>
        </div>

        {hasText ? <div className="bubble-body">{body}</div> : null}

        {attachmentUrl ? (
          <div className="bubble-attachment">
            <a href={attachmentUrl} target="_blank" rel="noreferrer">
              {t('messages.viewAttachment')}
            </a>
          </div>
        ) : null}

        <div className="bubble-actions">
          <button
            type="button"
            className="btn mini"
            disabled={!hasText || isTranslating}
            onClick={() => onTranslate?.(translateKey, body)}
            title={t('translate.title')}
          >
            {isTranslating ? t('translate.translating') : t('translate.translate')}
          </button>

          {hasTranslation ? (
            <button
              type="button"
              className="btn mini ghost"
              onClick={() => onToggleTranslation?.(translateKey)}
              title={t('translate.toggleTitle')}
            >
              {showTranslation ? t('translate.hideTranslation') : t('translate.showTranslation')}
            </button>
          ) : null}
        </div>

        {hasTranslation && showTranslation ? (
          <div className="translation-box">
            <div className="translation-label">{t('translate.translation')}</div>
            <div className="translation-text">{translatedText}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function DepartmentMessagesPortal() {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const locale = isZh ? 'zh-CN' : 'en-US';
  const [mode, setMode] = useState('department');

  const [step, setStep] = useState('login'); // login | verify | portal
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [department, setDepartment] = useState('image_processing');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');

  const [managerCode, setManagerCode] = useState('');

  const [token, setToken] = useState('');
  const [departmentLabel, setDepartmentLabel] = useState('');
  const [managerLabel, setManagerLabel] = useState(t('deptMsg.manager'));

  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const selectedThreadIdRef = useRef(null);

  const [loadingThreads, setLoadingThreads] = useState(false);

  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');

  const [patientOptions, setPatientOptions] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);

  //  optional patient search (server-side ?q=) + debounce
  const [patientQuery, setPatientQuery] = useState('');
  const patientQueryTimerRef = useRef(null);

  const [newPatientId, setNewPatientId] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [templateKey, setTemplateKey] = useState('');

  //  NEW: visibility checkbox for thread creation (default: hidden)
  const [newVisibleToDistributor, setNewVisibleToDistributor] = useState(false);

  //  thread-level hidden toggle for existing threads (manager only)
  const [threadHidden, setThreadHidden] = useState(false);
  const [savingThreadHidden, setSavingThreadHidden] = useState(false);

  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);

  //  reply-level hidden (department + manager)
  const [replyHidden, setReplyHidden] = useState(false);

  const [sendAsDept, setSendAsDept] = useState('');

  const [translateTo, setTranslateTo] = useState('zh-Hans');
  const [translationCache, setTranslationCache] = useState({});
  const [translatingKeys, setTranslatingKeys] = useState({});
  const [showTranslationKeys, setShowTranslationKeys] = useState({});

  const [mgrInitiatedThreads, setMgrInitiatedThreads] = useState(
    () => new Set(readJsonLocal('mgr_initiated_thread_ids', []))
  );
  const [mgrReplyIds, setMgrReplyIds] = useState(() => new Set(readJsonLocal('mgr_reply_ids', [])));

  const pendingManagerReplyBodyRef = useRef(null);
  const pollRef = useRef(null);

  //  Keep client_request_id stable across retries (prevents duplicates on “error but saved”)
  const pendingReplyRequestIdRef = useRef(null);
  const pendingStartRequestIdRef = useRef(null);

  const authHeaders = useMemo(() => buildAuthHeaders({ mode, token }), [mode, token]);

  const logout = () => {
    setError('');
    setToken('');
    setDepartmentLabel('');
    setManagerLabel(t('deptMsg.manager'));
    setThreads([]);
    setSelectedThread(null);
    selectedThreadIdRef.current = null;
    setStep('login');

    pendingReplyRequestIdRef.current = null;
    pendingStartRequestIdRef.current = null;

    localStorage.removeItem('dept_msg_token');
    localStorage.removeItem('dept_msg_department');
    localStorage.removeItem('dept_msg_department_label');

    localStorage.removeItem('mgr_msg_token');
    localStorage.removeItem('mgr_msg_label');

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const logoutWithMessage = (msg) => {
    logout();
    setError(msg || t('deptMsg.sessionExpired'));
  };

  const handleAuthError = (err, fallbackMsg) => {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      logoutWithMessage(fallbackMsg || t('deptMsg.sessionExpiredUnauthorized'));
      return true;
    }
    return false;
  };

  useEffect(() => {
    const deptToken = localStorage.getItem('dept_msg_token') || '';
    const deptLabel = localStorage.getItem('dept_msg_department_label') || '';
    const deptValue = localStorage.getItem('dept_msg_department') || '';

    const mgrToken = localStorage.getItem('mgr_msg_token') || '';
    const mgrLabel = localStorage.getItem('mgr_msg_label') || t('deptMsg.manager');

    if (deptToken) {
      setMode('department');
      setToken(deptToken);
      setDepartmentLabel(deptLabel);
      if (deptValue) setDepartment(deptValue);
      setStep('portal');
      return;
    }
    if (mgrToken) {
      setMode('manager');
      setToken(mgrToken);
      setManagerLabel(mgrLabel);
      setStep('portal');
    }
  }, []);

  useEffect(() => {
    setError('');
    setThreads([]);
    setSelectedThread(null);
    selectedThreadIdRef.current = null;

    setSearch('');
    setDeptFilter('all');

    setReplyBody('');
    setReplyFile(null);
    setReplyHidden(false);

    setSendAsDept('');
    setTemplateKey('');
    setNewPatientId('');
    setNewSubject('');
    setNewBody('');
    setNewFile(null);

    //  reset new thread visibility checkbox
    setNewVisibleToDistributor(false);

    setThreadHidden(false);
    setSavingThreadHidden(false);

    setTranslationCache({});
    setTranslatingKeys({});
    setShowTranslationKeys({});
    setTranslateTo('zh-Hans');

    setPatientOptions([]);
    setPatientQuery('');

    setToken('');
    setStep('login');

    pendingReplyRequestIdRef.current = null;
    pendingStartRequestIdRef.current = null;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (patientQueryTimerRef.current) {
      clearTimeout(patientQueryTimerRef.current);
      patientQueryTimerRef.current = null;
    }
  }, [mode]);

  const fetchThreads = useCallback(async () => {
    if (!token) return;

    setLoadingThreads(true);
    setError('');

    try {
      const url =
        mode === 'manager' ? 'department-messages/manager/threads/' : 'department-messages/threads/';

      const resp = await axios.get(url, { headers: authHeaders });
      const list = Array.isArray(resp.data) ? resp.data : [];

      setThreads(list);

      const selId = selectedThreadIdRef.current;
      if (selId) {
        const updated = list.find((t) => t.id === selId);
        if (updated) setSelectedThread((prev) => (prev ? { ...prev, ...updated } : prev));
      }
    } catch (err) {
      console.error('Failed to load threads', err);
      if (handleAuthError(err, t('deptMsg.errSessionExpired'))) return;
      setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errLoadThreads'));
    } finally {
      setLoadingThreads(false);
    }
  }, [token, mode, authHeaders]);

  const fetchThreadDetail = useCallback(
    async (threadId) => {
      if (!token || !threadId) return;

      setError('');
      try {
        const url =
          mode === 'manager'
            ? `department-messages/manager/threads/${threadId}/`
            : `department-messages/threads/${threadId}/`;

        const resp = await axios.get(url, { headers: authHeaders });

        setSelectedThread(resp.data);
        selectedThreadIdRef.current = resp.data?.id || threadId;

        //  keep thread-level hidden toggle synced (manager only)
        setThreadHidden(isThreadHidden(resp.data));

        if (mode === 'manager') {
          setSendAsDept(resp.data?.department || '');
        }

        if (mode === 'manager' && pendingManagerReplyBodyRef.current) {
          const pendingBody = pendingManagerReplyBodyRef.current;
          pendingManagerReplyBodyRef.current = null;

          const replies = resp.data?.replies || [];
          const match = [...replies].reverse().find((r) => (r.body || '').trim() === pendingBody.trim());

          if (match?.id) {
            setMgrReplyIds((prev) => {
              const next = new Set(prev);
              next.add(match.id);
              writeJsonLocal('mgr_reply_ids', Array.from(next));
              return next;
            });
          }
        }
      } catch (err) {
        console.error('Failed to load thread detail', err);
        if (handleAuthError(err, t('deptMsg.errSessionExpired'))) return;
        setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errLoadThreadDetail'));
      }
    },
    [token, mode, authHeaders]
  );

  const loadPatients = useCallback(
    async ({ q } = {}) => {
      if (!token) return;

      setPatientLoading(true);
      try {
        const url =
          mode === 'manager'
            ? 'department-messages/manager/patients/'
            : 'department-messages/patients/';

        const params = {};
        const trimmed = (q || '').trim();
        if (trimmed) params.q = trimmed;
        params.limit = 500;

        const resp = await axios.get(url, { headers: authHeaders, params });
        setPatientOptions(Array.isArray(resp.data) ? resp.data : []);
      } catch (err) {
        console.error('Failed to load patients', err);
        if (handleAuthError(err, t('deptMsg.errSessionExpired'))) return;
      } finally {
        setPatientLoading(false);
      }
    },
    [token, mode, authHeaders]
  );

  useEffect(() => {
    if (!token || step !== 'portal') return;
    fetchThreads();
    loadPatients({ q: '' });
  }, [token, step, fetchThreads, loadPatients]);

  useEffect(() => {
    if (!token || step !== 'portal') return;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    pollRef.current = setInterval(() => {
      fetchThreads();
    }, POLL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [token, step, fetchThreads]);

  //  Debounced patient search (server-side ?q=)
  useEffect(() => {
    if (!token || step !== 'portal') return;

    if (patientQueryTimerRef.current) {
      clearTimeout(patientQueryTimerRef.current);
      patientQueryTimerRef.current = null;
    }

    patientQueryTimerRef.current = setTimeout(() => {
      const q = (patientQuery || '').trim();
      if (!q || q.length >= 2) {
        loadPatients({ q });
      }
    }, PATIENT_SEARCH_DEBOUNCE_MS);

    return () => {
      if (patientQueryTimerRef.current) {
        clearTimeout(patientQueryTimerRef.current);
        patientQueryTimerRef.current = null;
      }
    };
  }, [patientQuery, token, step, loadPatients]);

  const handleDeptLoginRequest = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await axios.post('department-messages/login-request/', {
        department,
        email: email.trim() || undefined,
      });
      setStep('verify');
    } catch (err) {
      console.error('dept login-request failed', err);
      setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errSendCode'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeptLoginVerify = async (e) => {
    e.preventDefault();
    setError('');

    if (!code.trim()) {
      setError(t('deptMsg.errEnterCode'));
      return;
    }

    setBusy(true);
    try {
      const resp = await axios.post('department-messages/login-verify/', {
        department,
        code: code.trim(),
      });

      const t = resp.data?.token || '';
      setToken(t);
      setDepartmentLabel(resp.data?.department_label || '');

      localStorage.setItem('dept_msg_token', t);
      localStorage.setItem('dept_msg_department', department);
      localStorage.setItem('dept_msg_department_label', resp.data?.department_label || '');

      setStep('portal');
    } catch (err) {
      console.error('dept login-verify failed', err);
      setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errInvalidCode'));
    } finally {
      setBusy(false);
    }
  };

  const handleManagerLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!managerCode.trim()) {
      setError(t('deptMsg.errEnterManagerCode'));
      return;
    }

    setBusy(true);
    try {
      const resp = await axios.post('department-messages/manager/login/', {
        code: managerCode.trim(),
      });

      const t = resp.data?.token || '';
      setToken(t);
      setManagerLabel(resp.data?.manager_label || t('deptMsg.manager'));

      localStorage.setItem('mgr_msg_token', t);
      localStorage.setItem('mgr_msg_label', resp.data?.manager_label || t('deptMsg.manager'));

      setStep('portal');
    } catch (err) {
      console.error('manager login failed', err);
      setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errInvalidManagerCode'));
    } finally {
      setBusy(false);
    }
  };

  const translateMessage = useCallback(
    async (key, text) => {
      if (!key || !(text || '').trim()) return;

      const cached = translationCache[key];
      if (cached && cached.to === translateTo && cached.text) {
        setShowTranslationKeys((prev) => ({ ...prev, [key]: true }));
        return;
      }

      setTranslatingKeys((prev) => ({ ...prev, [key]: true }));
      setError('');

      const payloads = [
        { text, to: translateTo },
        { text, target_language: translateTo },
        { text, to_language: translateTo },
        { q: text, to: translateTo },
      ];

      try {
        let translated = '';
        let lastErr = null;

        for (const p of payloads) {
          try {
            const resp = await axios.post('translate/', p, { headers: authHeaders });
            translated = extractTranslatedText(resp.data);
            if (translated) break;
          } catch (e) {
            lastErr = e;
          }
        }

        if (!translated) {
          console.error('Translate failed', lastErr);
          if (handleAuthError(lastErr, t('deptMsg.errSessionExpired'))) return;
          setError(t('deptMsg.errTranslateFailed'));
          return;
        }

        setTranslationCache((prev) => ({
          ...prev,
          [key]: { to: translateTo, text: translated },
        }));
        setShowTranslationKeys((prev) => ({ ...prev, [key]: true }));
      } finally {
        setTranslatingKeys((prev) => ({ ...prev, [key]: false }));
      }
    },
    [translateTo, translationCache, authHeaders]
  );

  const toggleTranslation = (key) => {
    setShowTranslationKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredThreads = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return (threads || []).filter((t) => {
      if (mode === 'manager' && deptFilter !== 'all') {
        if ((t.department || '') !== deptFilter) return false;
      }
      if (!q) return true;

      const hay = [
        t.subject,
        t.body,
        t.patient_display,
        t.location_display,
        t.department_display,
        t.department,
        t.status_display,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [threads, search, mode, deptFilter]);

  const sortedPatientOptions = useMemo(() => {
    const arr = Array.isArray(patientOptions) ? [...patientOptions] : [];
    arr.sort((a, b) => patientLabel(a).toLowerCase().localeCompare(patientLabel(b).toLowerCase()));
    return arr;
  }, [patientOptions]);

  const onSelectThread = async (thread) => {
    if (!thread?.id) return;
    pendingReplyRequestIdRef.current = null;
    setReplyHidden(false);
    await fetchThreadDetail(thread.id);
  };

  //  manager-only: toggle thread hidden state for an existing thread
  const setThreadHiddenRemote = async (threadId, nextHidden) => {
    if (!token || mode !== 'manager' || !threadId) return;

    setSavingThreadHidden(true);
    setError('');

    try {
      const resp = await axios.post(
        `department-messages/manager/threads/${threadId}/set-hidden/`,
        { is_hidden: !!nextHidden },
        { headers: authHeaders }
      );

      if (resp?.data && typeof resp.data === 'object') {
        setSelectedThread((prev) => (prev?.id === threadId ? { ...prev, ...resp.data } : prev));
        setThreadHidden(isThreadHidden(resp.data));
      } else {
        setThreadHidden(!!nextHidden);
      }

      await fetchThreads();
    } catch (err) {
      console.error('Failed to update thread hidden', err);
      if (handleAuthError(err, t('deptMsg.errSessionExpired'))) return;
      throw err;
    } finally {
      setSavingThreadHidden(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selectedThread) return;

    setError('');
    if (!replyBody.trim() && !replyFile) {
      setError(t('deptMsg.errReplyRequired'));
      return;
    }

    setSendingReply(true);
    try {
      const reqId = pendingReplyRequestIdRef.current || newClientRequestId('reply');
      pendingReplyRequestIdRef.current = reqId;

      const formData = new FormData();
      formData.append('body', replyBody.trim());
      formData.append('client_request_id', reqId);
      if (replyFile) formData.append('attachment', replyFile);

      if (mode === 'manager') {
        const deptToUse = (sendAsDept || selectedThread.department || '').trim();
        if (deptToUse) formData.append('department', deptToUse);
        pendingManagerReplyBodyRef.current = replyBody.trim();
      }

      //  allow BOTH manager + department portals to hide a reply from distributor
      if (mode === 'manager' || mode === 'department') {
        appendHiddenReplyFlags(formData, replyHidden);
      }

      const url =
        mode === 'manager'
          ? `department-messages/manager/threads/${selectedThread.id}/reply/`
          : `department-messages/threads/${selectedThread.id}/reply/`;

      await axios.post(url, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });

      pendingReplyRequestIdRef.current = null;

      setReplyBody('');
      setReplyFile(null);
      setReplyHidden(false);

      await fetchThreads();
      await fetchThreadDetail(selectedThread.id);
    } catch (err) {
      console.error('Failed to send reply', err);
      if (handleAuthError(err, t('deptMsg.errSessionExpired'))) return;
      setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errSendReply'));
    } finally {
      setSendingReply(false);
    }
  };

  const handleTemplateChange = (e) => {
    const key = e.target.value;
    setTemplateKey(key);
    const tpl = TEMPLATE_PRESETS.find((t) => t.key === key);
    if (!tpl) return;
    if (!newSubject) setNewSubject(tpl.subjectKey ? t(tpl.subjectKey) : '');
    if (!newBody) setNewBody(tpl.bodyKey ? t(tpl.bodyKey) : '');
  };

  const handleStartThread = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPatientId) {
      setError(t('deptMsg.errPatientRequired'));
      return;
    }
    if (!newSubject.trim() || (!newBody.trim() && !newFile)) {
      setError(t('deptMsg.errNewThreadRequired'));
      return;
    }

    try {
      const reqId = pendingStartRequestIdRef.current || newClientRequestId('start');
      pendingStartRequestIdRef.current = reqId;

      const formData = new FormData();
      formData.append('patient', String(newPatientId));
      formData.append('subject', newSubject.trim());
      formData.append('body', newBody.trim());
      formData.append('client_request_id', reqId);
      if (newFile) formData.append('attachment', newFile);

      if (mode === 'manager') {
        const deptToUse = (sendAsDept || '').trim();
        if (!deptToUse) {
          setError(t('deptMsg.errSendAsRequired'));
          return;
        }
        formData.append('department', deptToUse);
      }

      //  NEW RULE:
      // - Department + Manager initiated threads are HIDDEN by default.
      // - Only visible if checkbox is checked.
      const isHidden = !newVisibleToDistributor;
      appendHiddenFlags(formData, isHidden);

      const url =
        mode === 'manager'
          ? 'department-messages/manager/threads/start/'
          : 'department-messages/threads/start/';

      const resp = await axios.post(url, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });

      pendingStartRequestIdRef.current = null;

      await fetchThreads();

      if (resp.data?.id) {
        if (mode === 'manager') {
          setMgrInitiatedThreads((prev) => {
            const next = new Set(prev);
            next.add(resp.data.id);
            writeJsonLocal('mgr_initiated_thread_ids', Array.from(next));
            return next;
          });
        }
        await fetchThreadDetail(resp.data.id);
      }

      setNewPatientId('');
      setNewSubject('');
      setNewBody('');
      setNewFile(null);
      setTemplateKey('');
      setNewVisibleToDistributor(false);
    } catch (err) {
      console.error('Failed to start new thread', err);
      if (handleAuthError(err, t('deptMsg.errSessionExpired'))) return;
      setError(!isZh && err.response?.data?.detail ? err.response.data.detail : t('deptMsg.errStartThread'));
    }
  };

  const pageTitle = mode === 'manager' ? t('deptMsg.managerConsole') : t('deptMsg.departmentMessaging');
  const pageSubtitle =
    mode === 'manager'
      ? t('deptMsg.managerSubtitle')
      : t('deptMsg.departmentSubtitle');

  const headerRight = (
    <div className="topbar-right">
      {step === 'portal' && token ? (
        <>
          <div className="topbar-pill">
            {mode === 'manager' ? (
              <>
                <ThreadPill tone="info">{managerLabel || t('deptMsg.manager')}</ThreadPill>
                <ThreadPill tone="neutral">{t('deptMsg.allDepartments')}</ThreadPill>
              </>
            ) : (
              <>
                <ThreadPill tone="info">{departmentLabel || t('department.department')}</ThreadPill>
                <ThreadPill tone="neutral">{department}</ThreadPill>
              </>
            )}
          </div>

          <button
            className="btn"
            type="button"
            onClick={async () => {
              await fetchThreads();
              if (selectedThread?.id) await fetchThreadDetail(selectedThread.id);
            }}
            title={t('refresh')}
          >
            {t('refresh')}
          </button>

          <div className="translate-bar">
            <span className="translate-label">{t('translate.to')}</span>
            <select value={translateTo} onChange={(e) => setTranslateTo(e.target.value)}>
              {TRANSLATE_TARGETS.map((target) => (
                <option key={target.value} value={target.value}>
                  {t(target.labelKey)}
                </option>
              ))}
            </select>
          </div>

          <button className="btn ghost" onClick={logout} type="button">
            {t('deptMsg.logout')}
          </button>
        </>
      ) : null}
    </div>
  );

  if (step === 'login') {
    return (
      <div className="msg-shell">
        <div className="auth-wrap">
          <div className="auth-hero">
            <div className="hero-badge">{t('deptMsg.asclepiusMessaging')}</div>
            <h1 className="hero-title">{pageTitle}</h1>
            <p className="hero-subtitle">{pageSubtitle}</p>

            <div className="mode-toggle">
              <button
                className={`btn toggle ${mode === 'department' ? 'active' : ''}`}
                onClick={() => setMode('department')}
                type="button"
              >
                {t('department.department')}
              </button>
              <button
                className={`btn toggle ${mode === 'manager' ? 'active' : ''}`}
                onClick={() => setMode('manager')}
                type="button"
              >
                {t('deptMsg.manager')}
              </button>
            </div>
          </div>

          <div className="card auth-card">
            {error ? <div className="alert error">{error}</div> : null}

            {mode === 'department' ? (
              <form onSubmit={handleDeptLoginRequest} className="form">
                <div className="field">
                  <label>{t('department.department')}</label>
                  <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                    {DEPT_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {t(d.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>{t('deptMsg.departmentEmailOptional')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="department@cranialtech.com"
                  />
                  <div className="hint">
                    {t('deptMsg.emailHint')}
                  </div>
                </div>

                <button className="btn primary" disabled={busy} type="submit">
                  {busy ? t('messages.sending') : t('deptMsg.sendCode')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleManagerLogin} className="form">
                <div className="field">
                  <label>{t('deptMsg.managerAccessCode')}</label>
                  <input
                    type="password"
                    value={managerCode}
                    onChange={(e) => setManagerCode(e.target.value)}
                    placeholder={t('deptMsg.managerCodePlaceholder')}
                  />
                  <div className="hint">{t('deptMsg.managerHint')}</div>
                </div>

                <button className="btn primary" disabled={busy} type="submit">
                  {busy ? t('deptMsg.signingIn') : t('deptMsg.signIn')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="msg-shell">
        <div className="auth-wrap single">
          <div className="card auth-card">
            <div className="auth-header">
              <div>
                <h1 className="title">{t('deptMsg.enterCode')}</h1>
                <p className="subtitle">
                  {t('deptMsg.checkMailbox', { department })}
                </p>
              </div>
              {headerRight}
            </div>

            {error ? <div className="alert error">{error}</div> : null}

            <form onSubmit={handleDeptLoginVerify} className="form">
              <div className="field">
                <label>{t('deptMsg.codeLabel')}</label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                />
              </div>

              <div className="row gap">
                <button className="btn primary" disabled={busy} type="submit">
                  {busy ? t('deptMsg.verifying') : t('deptMsg.verifyOpenInbox')}
                </button>
                <button className="btn ghost" type="button" onClick={() => setStep('login')}>
                  {t('back')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="msg-shell">
      <div className="topbar">
        <div>
          <div className="title-sm">{pageTitle}</div>
          <div className="subtitle-sm">{pageSubtitle}</div>
        </div>
        {headerRight}
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="grid">
        <div className="card list-card">
          <div className="list-top">
            <div className="list-title">
              {t('deptMsg.threads')}
              {loadingThreads ? <span className="spinner">{t('messages.refreshing')}</span> : null}
            </div>

            <div className="list-controls">
              <input
                className="search"
                placeholder={t('deptMsg.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {mode === 'manager' ? (
                <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                  <option value="all">{t('deptMsg.allDepartments')}</option>
                  {DEPT_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {t(d.labelKey)}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          </div>

          <div className="thread-list">
            {filteredThreads.length === 0 ? (
              <div className="empty">{t('deptMsg.noThreads')}</div>
            ) : (
              filteredThreads.map((thread) => {
                const isActive = selectedThread?.id === thread.id;
                const deptTone =
                  thread.department === 'manufacturing'
                    ? 'warn'
                    : thread.department === 'clinical_support'
                    ? 'ok'
                    : 'info';

                const initiatedByManager = mgrInitiatedThreads.has(thread.id);
                const hidden = isThreadHidden(thread);

                return (
                  <button
                    key={thread.id}
                    className={`thread-item ${isActive ? 'active' : ''}`}
                    onClick={() => onSelectThread(thread)}
                    type="button"
                  >
                    <div className="thread-row">
                      <div className="thread-main">
                        <div className="thread-subject">
                          {thread.subject || t('messages.noSubject')}
                          {mode === 'manager' && initiatedByManager ? (
                            <span className="manager-tag">{t('deptMsg.managerInitiated')}</span>
                          ) : null}
                          {hidden ? <span className="manager-tag hidden">{t('deptMsg.hidden')}</span> : null}
                        </div>
                        <div className="thread-meta">
                          <span className="muted">{thread.patient_display || t('deptMsg.patient')}</span>
                          {thread.location_display ? <span className="muted"> · {thread.location_display}</span> : null}
                        </div>
                      </div>
                      <div className="thread-side">
                        <ThreadPill tone={deptTone}>{thread.department_display || t(deptLabelFromValue(thread.department)) || t('deptMsg.dept')}</ThreadPill>
                        <div className="muted tiny">{safeDate(thread.created_at, locale)}</div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="card convo-card">
          {!selectedThread ? (
            <div className="empty convo-empty">
              <div className="empty-title">{t('deptMsg.selectThread')}</div>
              <div className="empty-sub">{t('deptMsg.selectThreadHint')}</div>
            </div>
          ) : (
            <>
              <div className="convo-header">
                <div className="convo-title">
                  <div className="convo-subject">{selectedThread.subject || t('messages.noSubject')}</div>
                  <div className="convo-meta">
                    <span className="muted">{selectedThread.patient_display || t('deptMsg.patient')}</span>
                    {selectedThread.location_display ? (
                      <span className="muted"> · {selectedThread.location_display}</span>
                    ) : null}
                    <span className="muted">
                      {' '}
                      · {t('deptMsg.status')}: <strong>{selectedThread.status_display || selectedThread.status || ''}</strong>
                    </span>
                    {isThreadHidden(selectedThread) ? (
                      <span className="muted">
                        {' '}
                        · <strong>{t('deptMsg.hidden')}</strong>
                      </span>
                    ) : null}
                  </div>
                </div>

                {mode === 'manager' ? (
                  <div className="convo-tools">
                    <div className="send-as">
                      <label>{t('deptMsg.sendAs')}</label>
                      <select
                        value={sendAsDept || selectedThread.department || ''}
                        onChange={(e) => setSendAsDept(e.target.value)}
                      >
                        <option value="">{t('deptMsg.defaultThreadDept')}</option>
                        {DEPT_OPTIONS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {t(d.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/*  manager-only thread hidden toggle */}
                    <div className="thread-flags">
                      <label className="pm-toggle">
                        <input
                          type="checkbox"
                          checked={threadHidden}
                          disabled={savingThreadHidden}
                          onChange={async (e) => {
                            const next = e.target.checked;
                            const prev = threadHidden;

                            setThreadHidden(next);
                            try {
                              await setThreadHiddenRemote(selectedThread.id, next);
                              await fetchThreadDetail(selectedThread.id);
                            } catch (err) {
                              setThreadHidden(prev);
                              setError(
                                (!isZh && err?.response?.data?.detail ? err.response.data.detail : t('deptMsg.errUpdateHidden'))
                              );
                            }
                          }}
                        />
                        <span>{savingThreadHidden ? t('deptMsg.saving') : t('deptMsg.hiddenInternalOnly')}</span>
                      </label>
                      <div className="hint" style={{ marginTop: 6 }}>
                        {t('deptMsg.hideThreadHint')}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="convo-body">
                {(() => {
                  const rootKey = `thread-${selectedThread.id}-root`;
                  const isManagerRoot = !!selectedThread.via_manager;

                  const deptName = t(deptLabelFromValue(selectedThread.department || sendAsDept));
                  const header = isManagerRoot
                    ? t('deptMsg.messagingManagerSlash', { department: deptName })
                    : selectedThread.sender_display || t('messages.distributor');

                  const subheader = isManagerRoot ? t('deptMsg.internalSenderManager') : '';

                  const badges = [];
                  if (isThreadHidden(selectedThread)) badges.push(t('deptMsg.hidden'));

                  return (
                    <ConversationBubble
                      t={t}
                      side={isManagerRoot ? 'right' : 'left'}
                      header={header}
                      subheader={subheader}
                      time={safeDate(selectedThread.created_at, locale)}
                      body={selectedThread.body || ''}
                      attachmentUrl={selectedThread.attachment || ''}
                      onTranslate={translateMessage}
                      translateKey={rootKey}
                      translatedText={translationCache[rootKey]?.to === translateTo ? translationCache[rootKey]?.text : ''}
                      isTranslating={!!translatingKeys[rootKey]}
                      showTranslation={!!showTranslationKeys[rootKey]}
                      onToggleTranslation={toggleTranslation}
                      badges={badges}
                    />
                  );
                })()}

                {/*  FIX: dedupe replies so the root message doesn't show up twice */}
                {(() => {
                  const raw = Array.isArray(selectedThread.replies) ? selectedThread.replies : [];

                  const rootBody = (selectedThread.body || '').trim();
                  const rootAttach = (selectedThread.attachment || '').trim();
                  const rootTs = selectedThread.created_at ? Date.parse(selectedThread.created_at) : NaN;

                  const seen = new Set();

                  const deduped = raw.filter((r, idx) => {
                    if (!r) return false;

                    const body = (r.body || '').trim();
                    const attach = (r.attachment || '').trim();
                    const ts = r.created_at ? Date.parse(r.created_at) : NaN;

                    const sameBody = !!rootBody && !!body && body === rootBody;
                    const sameAttach = (rootAttach || '') === (attach || '');
                    const closeInTime =
                      !Number.isNaN(rootTs) && !Number.isNaN(ts) && Math.abs(ts - rootTs) <= 5000; // 5s window

                    // If the backend accidentally includes the root message inside replies, skip it.
                    const looksLikeRootDuplicate = sameBody && sameAttach && (closeInTime || idx === 0);
                    if (looksLikeRootDuplicate) return false;

                    // Also dedupe duplicates inside replies themselves
                    const sig = [
                      r.id ? `id:${r.id}` : '',
                      body ? `b:${body}` : '',
                      attach ? `a:${attach}` : '',
                      !Number.isNaN(ts) ? `t:${Math.floor(ts / 1000)}` : '',
                      r.department ? `d:${r.department}` : '',
                      r.author_type ? `at:${String(r.author_type)}` : '',
                    ]
                      .filter(Boolean)
                      .join('|');

                    if (sig && seen.has(sig)) return false;
                    if (sig) seen.add(sig);

                    return true;
                  });

                  return deduped.map((r) => {
                    const isDept =
                      String(r.author_type || '').toLowerCase().includes('department') || !!r.department;

                    const side = isDept ? 'right' : 'left';

                    const deptName = t(deptLabelFromValue(r.department || selectedThread.department || sendAsDept));
                    const isManagerReply = !!r.via_manager || (mode === 'manager' && r.id && mgrReplyIds.has(r.id));

                    const header = isDept
                      ? isManagerReply
                        ? t('deptMsg.messagingManagerSlash', { department: deptName })
                        : r.department_display || deptName
                      : r.author_display || t('messages.distributor');

                    const subheader = isDept && isManagerReply ? t('deptMsg.internalSenderManager') : '';

                    const key = r.id ? `reply-${r.id}` : `reply-${selectedThread.id}-${r.created_at || 'unknown'}`;

                    const badges = [];
                    if (isThreadHidden(selectedThread)) badges.push(t('deptMsg.hidden'));
                    if (isReplyHidden(r)) badges.push(t('deptMsg.hiddenReply'));

                    return (
                      <ConversationBubble
                        t={t}
                        key={r.id || key}
                        side={side}
                        header={header}
                        subheader={subheader}
                        time={safeDate(r.created_at, locale)}
                        body={r.body || ''}
                        attachmentUrl={r.attachment || ''}
                        onTranslate={translateMessage}
                        translateKey={key}
                        translatedText={translationCache[key]?.to === translateTo ? translationCache[key]?.text : ''}
                        isTranslating={!!translatingKeys[key]}
                        showTranslation={!!showTranslationKeys[key]}
                        onToggleTranslation={toggleTranslation}
                        badges={badges}
                      />
                    );
                  });
                })()}
              </div>

              <div className="convo-compose">
                <form onSubmit={handleSendReply} className="compose">
                  <div className="compose-row">
                    <textarea
                      rows={3}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder={t('deptMsg.typeReply')}
                    />
                  </div>

                  {(mode === 'manager' || mode === 'department') ? (
                    <div className="compose-footnote">
                      <label className="pm-toggle">
                        <input
                          type="checkbox"
                          checked={replyHidden}
                          onChange={(e) => setReplyHidden(e.target.checked)}
                        />
                        <span>{t('deptMsg.hideReplyInternal')}</span>
                      </label>
                      <div className="hint" style={{ marginTop: 6 }}>
                        {t('deptMsg.hideReplyHint')}
                      </div>
                    </div>
                  ) : null}

                  <div className="compose-actions">
                    <div className="file">
                      <label className="muted">{t('attachment')}</label>
                      <input type="file" onChange={(e) => setReplyFile(e.target.files?.[0] || null)} />
                    </div>

                    <button className="btn primary" type="submit" disabled={sendingReply}>
                      {sendingReply ? t('messages.sending') : t('messages.sendReply')}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>

        <div className="card new-thread-card">
          <div className="section-title">
            {t('deptMsg.startNew')}
            {patientLoading ? <span className="spinner">{t('deptMsg.loadingPatients')}</span> : null}
          </div>

          <div className="row gap wrap" style={{ marginBottom: 10 }}>
            <div className="field grow">
              <label>{t('deptMsg.searchPatients')}</label>
              <input
                value={patientQuery}
                onChange={(e) => setPatientQuery(e.target.value)}
                placeholder={t('deptMsg.patientSearchPlaceholder')}
              />
              <div className="hint">{t('deptMsg.patientSearchHint')}</div>
            </div>
            <div className="actions" style={{ marginTop: 22 }}>
              <button
                type="button"
                className="btn"
                onClick={() => loadPatients({ q: patientQuery })}
                disabled={patientLoading}
                title={t('deptMsg.reloadPatientsTitle')}
              >
                {t('reload')}
              </button>
            </div>
          </div>

          <form onSubmit={handleStartThread} className="new-thread-form">
            <div className="row gap wrap">
              <div className="field grow">
                <label>{t('deptMsg.patient')}</label>
                <select value={newPatientId} onChange={(e) => setNewPatientId(e.target.value)}>
                  <option value="">{t('deptMsg.select')}</option>
                  {sortedPatientOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {patientLabel(p, t('deptMsg.patient'))}
                      {p.location_display ? ` · ${p.location_display}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {mode === 'manager' ? (
                <div className="field">
                  <label>{t('deptMsg.sendAs')}</label>
                  <select value={sendAsDept} onChange={(e) => setSendAsDept(e.target.value)}>
                    <option value="">{t('deptMsg.selectDepartment')}</option>
                    {DEPT_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {t(d.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/*  NEW: visible checkbox (default = hidden) */}
              <label className="pm-toggle" style={{ marginTop: 18 }}>
                <input
                  type="checkbox"
                  checked={newVisibleToDistributor}
                  onChange={(e) => setNewVisibleToDistributor(e.target.checked)}
                />
                <span>{t('deptMsg.visibleToDistributor')}</span>
              </label>

              <div className="field">
                <label>{t('deptMsg.template')}</label>
                <select value={templateKey} onChange={handleTemplateChange}>
                  {TEMPLATE_PRESETS.map((tpl) => (
                    <option key={tpl.key} value={tpl.key}>
                      {t(tpl.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="hint" style={{ marginTop: 6 }}>
              {t('deptMsg.defaultVisibilityHintBefore')} <b>{t('deptMsg.hidden')}</b> {t('deptMsg.defaultVisibilityHintAfter')}
              “{t('deptMsg.visibleToDistributor')}”.
            </div>

            <div className="row gap wrap" style={{ marginTop: 10 }}>
              <div className="field grow">
                <label>{t('subject')}</label>
                <input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} />
              </div>
            </div>

            <div className="row gap wrap">
              <div className="field grow">
                <label>{t('message')}</label>
                <textarea rows={3} value={newBody} onChange={(e) => setNewBody(e.target.value)} />
              </div>
            </div>

            <div className="row gap wrap">
              <div className="field">
                <label>{t('attachmentOptional')}</label>
                <input type="file" onChange={(e) => setNewFile(e.target.files?.[0] || null)} />
              </div>

              <div className="actions">
                <button className="btn primary" type="submit">
                  {t('deptMsg.startThread')}
                </button>
                <button
                  className="btn ghost"
                  type="button"
                  onClick={() => {
                    pendingStartRequestIdRef.current = null;
                    setNewPatientId('');
                    setNewSubject('');
                    setNewBody('');
                    setNewFile(null);
                    setTemplateKey('');
                    setNewVisibleToDistributor(false);
                  }}
                >
                  {t('clear')}
                </button>
              </div>
            </div>
          </form>

          <div className="hint">
            {t('deptMsg.managerModeHint')}
          </div>
        </div>
      </div>
    </div>
  );
}

